import { useLayoutEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

type ILock = {
  key: string
  columnCount: number
  widths: number[]
  total: number
  fits: boolean
  containerWidth: number
}

export type IUseColumnWidthLockParams = {
  enabled: boolean
  columnsKey: string
  columnCount: number
  scrollElement: HTMLElement | null
  canMeasure: boolean
}

const isOverflowing = (element: HTMLElement) => element.scrollWidth > element.clientWidth

export const useColumnWidthLock = ({
  enabled,
  columnsKey,
  columnCount,
  scrollElement,
  canMeasure,
}: IUseColumnWidthLockParams) => {
  const [lock, setLock] = useState<ILock | null>(null)
  const activeLock =
    lock && lock.key === columnsKey && lock.columnCount === columnCount ? lock : null
  const activeLockRef = useRef<ILock | null>(null)

  useLayoutEffect(() => {
    activeLockRef.current = activeLock
  }, [activeLock])

  useLayoutEffect(() => {
    if (!enabled || activeLock || !canMeasure) return

    const headRow = scrollElement?.querySelector('thead tr')

    if (!scrollElement || !headRow) return

    const widths = Array.from(headRow.children).map((cell) => cell.getBoundingClientRect().width)

    if (!columnCount || widths.length !== columnCount) return

    if (!widths.every((width) => width > 0)) return

    setLock({
      key: columnsKey,
      columnCount,
      widths,
      total: widths.reduce((sum, width) => sum + width, 0),
      containerWidth: scrollElement.clientWidth,
      fits: !isOverflowing(scrollElement),
    })
  }, [enabled, activeLock, canMeasure, columnsKey, columnCount, scrollElement])

  useLayoutEffect(() => {
    if (!enabled || !scrollElement) return

    const observer = new ResizeObserver(() => {
      const current = activeLockRef.current

      if (!current) return

      const widthsStillValid = current.fits
        ? scrollElement.clientWidth === current.containerWidth
        : isOverflowing(scrollElement)

      if (widthsStillValid) return

      activeLockRef.current = null
      flushSync(() => setLock(null))
    })

    observer.observe(scrollElement)

    return () => observer.disconnect()
  }, [enabled, scrollElement])

  const renderColgroup = () => {
    if (!activeLock) return null

    const total = activeLock.total || 1

    return (
      <colgroup>
        {activeLock.widths.map((width, index) => (
          <col
            key={index}
            style={{ width: activeLock.fits ? `${(width / total) * 100}%` : `${width}px` }}
          />
        ))}
      </colgroup>
    )
  }

  return {
    activeLock,
    applyLock: enabled && !!activeLock,
    renderColgroup,
  }
}
